import {
  faCircleCheck,
  faCircleXmark,
  faEllipsisV,
  faPen,
  faTrashAlt
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import { useAtom } from 'jotai';
import type React from 'react';
import { useMemo, useRef } from 'react';

import ContextMenu, {
  type ContextMenuItem,
  type ContextMenuRefType
} from '@/components/ContextMenu';
import PERMISSIONS from '@/constants/auth.constants';
import { useCloudSourceActivation } from '@/features/CloudSourcesTable/hooks/useCloudSourceActivation.hook';
import { useStyles } from '@/features/DevicesTable';
import SOURCE_TYPE, {
  isMicrosoftChildSourceType,
  isReadOnlyManagedSourceType
} from '@/features/SourceCreate/SourceCreate.constants';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import { SourceDialogMode } from '@/features/SourceCreate/SourceCreate.types';
import { useDeviceSelectionParams } from '@/pages/Administration/Devices/Devices.hooks';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import checkPermissions from '@/services/permissions';
import roleAtom from '@/store/role';
import userAtom from '@/store/user.store';

import {
  filteredLimaSourcesAtom,
  selectedCloudSourceAtom,
  selectedMicrosoftSourceDetailsAtom,
  selectedSourceDetailsAtom
} from '../Devices.store';

export default function SourceDetailContextMenu() {
  const { classes } = useStyles();
  const childRef = useRef<ContextMenuRefType>(null);
  // subscribe so role changes re-render and re-evaluate access
  useAtom(roleAtom);
  const [user] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { openSourceDialog } = useSourceDialog();
  const { handleSingleSourceToggle, ConfirmActivateDeactivateModal } =
    useCloudSourceActivation();
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);

  const [selectedSourceDetail] = useAtom(selectedSourceDetailsAtom);
  const [, setSelectedCloudSource] = useAtom(selectedCloudSourceAtom);
  const [selectedMicrosoftSourceDetail] = useAtom(
    selectedMicrosoftSourceDetailsAtom
  );
  const { sourceUid } = useDeviceSelectionParams();

  const [filteredLimaSources] = useAtom(filteredLimaSourcesAtom);

  const selectedSource = useMemo(
    () =>
      filteredLimaSources.find((src) => src.source_uid === sourceUid) ||
      filteredLimaSources
        .flatMap((src) => src.data_providers || [])
        .find((provider) => provider.source_uid === sourceUid),
    [filteredLimaSources, sourceUid]
  );

  const srcTypeId = selectedSource?.source_type_id;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (childRef.current) {
      childRef.current.showContextMenu(event.currentTarget);
    }
  };

  const handleActivateDeactivate = async (isActive: boolean) => {
    let selectedSrcUids: string[] = [];
    if (srcTypeId !== SOURCE_TYPE.MICROSOFT) {
      // Handle non-Microsoft sources
      const uid = selectedSourceDetail?.source_uid;
      if (uid) {
        selectedSrcUids = [uid];
      }
    } else if (srcTypeId === SOURCE_TYPE.MICROSOFT) {
      selectedSrcUids =
        selectedMicrosoftSourceDetail
          ?.map((child) => child.source_uid)
          .filter((uid): uid is string => Boolean(uid)) || [];
    }
    if (!selectedSrcUids.length) {
      return;
    }

    const actionLabel = isActive ? 'Activate' : 'Deactivate';
    const confirmationMsg = `${actionLabel} the selected ${isActive ? 'inactive' : 'active'} source?`;

    await handleSingleSourceToggle({
      source_uids: selectedSrcUids,
      activate: isActive,
      confirmation_msg: confirmationMsg
    });
  };

  const onClickHandler = (selectedMenu: string) => {
    if (selectedSource) {
      setSelectedCloudSource(selectedSource);
    }

    if (selectedMenu === 'Edit') {
      openSourceDialog(SourceDialogMode.Edit, '', srcTypeId as number);
    } else if (selectedMenu === 'Delete') {
      openSourceDialog(SourceDialogMode.Delete, '', srcTypeId as number);
    } else if (selectedMenu === 'Activate') {
      handleActivateDeactivate(true);
    } else if (selectedMenu === 'Deactivate') {
      handleActivateDeactivate(false);
    }
  };

  const menuList = (() => {
    const baseMenu: ContextMenuItem[] = [
      {
        label: 'Edit',
        icon: faPen,
        permission: PERMISSIONS.SOURCES_UPDATE
      },
      {
        label: 'Delete',
        icon: faTrashAlt,
        permission: PERMISSIONS.SOURCES_DELETE
      }
    ];

    let isActive = false;
    if (isReadOnlyManagedSourceType(srcTypeId)) {
      return [];
    }

    // Remove the "Edit" option for Microsoft child source types
    if (
      isMicrosoftChildSourceType(srcTypeId) &&
      selectedSource?.parent_source_uid != null
    ) {
      baseMenu.splice(0, 1);
    }

    if (srcTypeId === SOURCE_TYPE.MICROSOFT) {
      // For Microsoft sources, check if ANY child is active
      if (
        selectedMicrosoftSourceDetail &&
        selectedMicrosoftSourceDetail.length > 0
      ) {
        isActive = selectedMicrosoftSourceDetail.some(
          (child) => child.active === true
        );
      }
    } else if (srcTypeId !== undefined) {
      // For non-Microsoft sources, use the direct active property
      isActive = selectedSourceDetail?.active ?? false;
    }

    const activeStateMenu: ContextMenuItem[] =
      isActive === true
        ? [
            {
              label: 'Deactivate',
              icon: faCircleXmark,
              permission: PERMISSIONS.SOURCES_UPDATE,
              disabled:
                Number(selectedOrgData.id) <= 0 ||
                !selectedSource?.allowSourceActivation
            }
          ]
        : [
            {
              label: 'Activate',
              icon: faCircleCheck,
              permission: PERMISSIONS.SOURCES_UPDATE,
              disabled:
                Number(selectedOrgData.id) <= 0 ||
                !selectedSource?.allowSourceActivation
            }
          ];
    return [...baseMenu, ...activeStateMenu];
  })();

  const accessCount = menuList.filter((item) =>
    checkPermissions(item.permission, user, selectedOrganization)
  ).length;

  return accessCount > 0 ? (
    <>
      <Button
        className={classes.ellipsisIcoBtn}
        size="medium"
        variant="outlined"
        id="basic-button"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <FontAwesomeIcon
          icon={faEllipsisV}
          size="lg"
          style={{ margin: '1px' }}
        />
      </Button>

      <ContextMenu
        key={`source-detail-context-menu-${srcTypeId}-${JSON.stringify(menuList.map((m) => m.label))}`}
        ref={childRef}
        menuList={menuList}
        className={classes.ellipsisMenu}
        clickHandler={onClickHandler}
      />
      <ConfirmActivateDeactivateModal />
    </>
  ) : null;
}
